import { useTranslation } from 'react-i18next'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Layout, MenuProps, notification, Typography } from 'antd'
import { colorText } from 'src/app/themes/primaryThemeDefault'
import { TableColumnDefs } from 'src/app/shared/components/common/table/table.model'
import { EyeIcon, RestoreIcon } from 'src/assets/icons'
import Table from 'src/app/shared/components/common/table'
import { menuUrls } from 'src/app/shared/constants/menu.constants'

import { useRestoreHelpPage, useVersionStory } from '../useHelpPage'
import HelpHeader from '../components/help-header'
import dayjs from 'dayjs'
import { FORMAT_DATE } from '../../../shared/constants/app.constants'
import { STATUS, VersionStatusTag } from '../components/TagComponents'
import SchedulePublishingModal, { ISchedulePublishing, State } from '../components/SchedulePublishingModal'
import { SYSTEM_MESSAGE_IDS } from '../../../shared/constants/systemMessages.constants'
import { useDefaultSearchData } from '../../../shared/components/common/table/table.constants'
import '../index.scss'
import { EPermissions, ESubModules } from '../../../shared/models/roleAndPermission.model'
import { useRolePermission } from '../../../shared/hooks/useRolePermission'
import RichTextField from 'src/app/shared/components/common/rich-text-field'

const VersionHistory: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const params = useParams()
  const { id } = params
  const location = useLocation()
  const { state } = location
  const { userPermissions } = useRolePermission()

  const { versionStoryList, totalRecords, isLoading, getVersionStoryListData } = useVersionStory()
  const [api, contextHolder] = notification.useNotification()
  const { isLoading: isLoadingRestore, restoreHelpPage } = useRestoreHelpPage()
  const restoreModalRef = useRef<{
    openModal: Function
    closeModal: Function
    setDataDefaultForm: (data: ISchedulePublishing) => any
  }>(null)
  const idHelpPageVersion = useRef<string>()
  const defaultSearchData = useDefaultSearchData()
  const versionRestore = useRef<number>()

  const getHeaderName = useCallback(
    (field: string): string => {
      return t(`HelpPage:viewVersionHistory.tableColumns.${field}`)
    },
    [t],
  )

  const getItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        {
          key: '1',
          label: (
            <Typography.Text style={{ fontSize: '14px' }}>
              {t(`HelpPage:viewVersionHistory.label.viewVersion`)}
            </Typography.Text>
          ),
          icon: <EyeIcon />,
          onClick: () => {
            if (rowData?.id) navigate(`${menuUrls.helpPageManagement}/view/${rowData?.id}`)
          },
        },
        userPermissions[ESubModules.HELP_PAGE_MANAGE]?.includes(EPermissions.ADD_EDIT_ITEMS) && !rowData.isCurrent
          ? {
              key: '2',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: colorText }}>
                  {t(`HelpPage:viewVersionHistory.label.restoreVersion`)}
                </Typography.Text>
              ),
              icon: <RestoreIcon />,
              onClick: () => {
                if (rowData?.id) {
                  versionRestore.current = rowData.version
                  if (rowData.status === STATUS.SCHEDULED) {
                    idHelpPageVersion.current = rowData?.id

                    if (restoreModalRef.current) {
                      restoreModalRef.current.setDataDefaultForm({
                        publishing: State.publishNow,
                        schedulePublishing: null,
                      })
                      restoreModalRef.current.openModal()
                    }
                  } else {
                    handleRestore(undefined, rowData.versionStatus, rowData.version)
                  }
                }
              },
            }
          : null,
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps

    [t, navigate],
  )

  const columnDefs: Array<TableColumnDefs> = useMemo(() => {
    return [
      {
        colId: 'version',
        field: 'version',
        headerName: t(getHeaderName('version')),
        headerClass: '',
        cellRenderer: (props: any) => {
          const { data } = props
          return data.isCurrent ? (
            <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
              {`${t(`HelpPage:viewVersionHistory.label.currentVersion`)} (v.${data.version})`}
            </Typography.Text>
          ) : (
            `v.${data.version}`
          )
        },
        sortable: true,
        resizable: true,
        hide: false,
        defaultSelect: true,
        sortIndex: 0,
        sortingOrder: ['asc', 'desc'],
      },
      {
        colId: 'versionStatus',
        field: 'versionStatus',
        headerClass: 'custom-header-center',
        headerName: t(getHeaderName('versionStatus')),
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
        },
        cellRenderer: (props: any) => {
          const { data } = props
          return <VersionStatusTag status={data.versionStatus} />
        },
        sortable: false,
        resizable: true,
        hide: false,
        defaultSelect: true,
      },
      {
        colId: 'scheduledOn',
        field: 'scheduledOn',
        headerName: t(getHeaderName('scheduledOn')),
        headerClass: 'custom-header-center',
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
        },
        cellRenderer: (props: any) => {
          const { data } = props
          return data.scheduledOn ? dayjs(data.scheduledOn).format(FORMAT_DATE) : ''
        },
        sortable: false,
        resizable: true,
        hide: false,
        defaultSelect: true,
      },
      {
        colId: 'lastUpdated',
        field: 'lastUpdated',
        headerName: t(getHeaderName('lastUpdated')),
        headerClass: 'custom-header-center',
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
        },
        cellRenderer: (props: any) => {
          const { data } = props
          return data.lastUpdated ? dayjs(data.lastUpdated).format(FORMAT_DATE) : ''
        },
        sortable: false,
        resizable: true,
        hide: false,
        defaultSelect: true,
      },
      {
        colId: 'updatedBy',
        field: 'updatedBy',
        headerName: t(getHeaderName('updatedBy')),
        sortable: false,
        resizable: true,
        hide: false,
        defaultSelect: true,
      },
    ]
  }, [getHeaderName, t])

  const handleRestore = async (data?: ISchedulePublishing, status?: STATUS, version?: number) => {
    if (data) {
      const res = await restoreHelpPage({
        id: id as string,
        publishSchedule: data.schedulePublishing ?? null,
        status: data?.schedulePublishing ? STATUS.SCHEDULED : STATUS.PUBLISHED,
      })

      if (res.isSuccess) {
        if (restoreModalRef.current) {
          restoreModalRef.current.closeModal()
        }
      }
    } else {
      const res = await restoreHelpPage({
        id: id as string,
        publishSchedule: null,
        status: status as number,
      })

      if (res.isSuccess) {
        if (restoreModalRef.current) {
          restoreModalRef.current.closeModal()
        }
      }
    }

    let message: string = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.RESTORE_VERSION_SUCCESSFULLY, {
      RESTORE_VERSION_SUCCESSFULLY: `v.${versionRestore.current}`,
    })

    api.success({
      message: <RichTextField richText={message} />,
    })
    getVersionStoryListData(id as string, defaultSearchData)
  }

  const handleSearch = useCallback(async (payload: Partial<any>) => {
    const newPayload = {
      ...defaultSearchData,
      ...payload,
      searchColumns: ['version', 'status', 'publishSchedule', 'updatedOn', 'updatedByName'],
      searchTerm: '',
      sortColumnsDictionary: { version: payload.isDesc ? 'desc' : 'asc' },
    }
    return await getVersionStoryListData(id as string, newPayload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (state?.helpPageManagement?.message) {
      api.success({
        message: <RichTextField richText={state?.helpPageManagement?.message} />,
      })
    }
    window.history.replaceState({}, '')
  }, [state?.helpPageManagement])

  useEffect(() => {
    if (id) {
      getVersionStoryListData(id, defaultSearchData)
    }
  }, [])

  useEffect(() => {
    if (state?.versionHistory?.message) {
      api.success({
        message: <RichTextField richText={state?.versionHistory?.message} />,
      })
    }
    window.history.replaceState({}, '')
  }, [state?.versionHistory])

  return (
    <Layout className={'view-version-history-screen'}>
      <HelpHeader
        title={t(`HelpPage:viewVersionHistory.label.viewVersionHistory`)}
        isShowBackIcon={true}
        onGoBack={() => navigate(menuUrls.helpPageManagement)}
      />

      <Layout.Content style={{ padding: 24 }}>
        {contextHolder}

        <Table
          loading={isLoading}
          isGettingList={isLoading}
          isUpdatingSetting={isLoading}
          isGettingSetting={isLoading}
          columnDefs={columnDefs}
          rowData={versionStoryList}
          displayAddBtn={false}
          actionDropdownItems={getItems}
          displayUploadButton={false}
          handleChooseColumn={async () => await {}}
          total={totalRecords}
          handleSearch={handleSearch}
          autoSort={false}
          getHeaderName={getHeaderName}
        />
      </Layout.Content>

      <SchedulePublishingModal onConfirm={handleRestore} ref={restoreModalRef} confirmLoading={isLoadingRestore} />
    </Layout>
  )
}

export default VersionHistory
